/**
 * LoadingSkeleton - Skeleton loading components
 *
 * Reusable skeleton components for loading states with Tailwind CSS
 */

import React from 'react';
import clsx from 'clsx';

// ============================================================================
// Base Skeleton Component
// ============================================================================

export interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  circle?: boolean;
  count?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  width,
  height,
  circle = false,
  count = 1,
}) => {
  const skeletonClass = clsx(
    'animate-pulse bg-gray-200',
    circle ? 'rounded-full' : 'rounded',
    className
  );

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  if (count === 1) {
    return <div className={skeletonClass} style={style} />;
  }

  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className={skeletonClass} style={style} />
      ))}
    </>
  );
};

// ============================================================================
// Form Field Skeleton
// ============================================================================

export const FormFieldSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="mb-4">
          {/* Label */}
          <Skeleton width="30%" height={16} className="mb-2" />
          {/* Input */}
          <Skeleton width="100%" height={40} />
        </div>
      ))}
    </>
  );
};

// ============================================================================
// Text Skeleton
// ============================================================================

export const TextSkeleton: React.FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className,
}) => {
  return (
    <div className={clsx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton key={index} width={index === lines - 1 ? '75%' : '100%'} height={16} />
      ))}
    </div>
  );
};

// ============================================================================
// Card Skeleton
// ============================================================================

export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-6 mb-4">
          <Skeleton width="40%" height={20} className="mb-4" />
          <TextSkeleton lines={3} />
        </div>
      ))}
    </>
  );
};

// ============================================================================
// Table Skeleton
// ============================================================================

export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {Array.from({ length: columns }).map((_, colIndex) => (
              <th key={colIndex} className="px-6 py-3 text-left">
                <Skeleton width="80%" height={16} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="px-6 py-4">
                  <Skeleton width="90%" height={16} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Loading Spinner
// ============================================================================

export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  text?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ size = 'md', className, text }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  return (
    <div className={clsx('flex flex-col items-center justify-center', className)}>
      <svg
        className={clsx('animate-spin text-blue-600', sizeClasses[size])}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {text && <p className="mt-2 text-sm text-gray-600">{text}</p>}
    </div>
  );
};

// ============================================================================
// Navigation Progress Bar (NProgress-style top bar)
// ============================================================================

export const NavigationProgressBar: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div
      className={clsx('fixed top-0 left-0 right-0 z-[9999] h-[3px]', className)}
      role="progressbar"
      aria-label="Page loading"
    >
      {/* Background track */}
      <div className="absolute inset-0 bg-primary/10" />
      {/* Animated progress indicator */}
      <div
        className="absolute inset-y-0 left-0 bg-primary rounded-r-full"
        style={{
          animation: 'ecg-nav-progress 2s ease-in-out infinite',
          boxShadow: '0 0 10px hsl(var(--primary) / 0.5), 0 0 5px hsl(var(--primary) / 0.3)',
        }}
      />
      {/* Shimmer glow effect */}
      <div
        className="absolute inset-y-0 w-24 bg-gradient-to-r from-transparent via-white/40 to-transparent"
        style={{
          animation: 'ecg-nav-shimmer 1.5s ease-in-out infinite',
        }}
      />
      <style>{`
        @keyframes ecg-nav-progress {
          0% { width: 0%; }
          20% { width: 35%; }
          50% { width: 65%; }
          80% { width: 85%; }
          100% { width: 95%; }
        }
        @keyframes ecg-nav-shimmer {
          0% { left: -10%; }
          100% { left: 110%; }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// Navigation Loading Overlay
// ============================================================================

export const NavigationLoadingOverlay: React.FC<{ text?: string; className?: string }> = ({
  text,
  className,
}) => {
  return (
    <div
      className={clsx(
        'fixed inset-0 z-[9998]',
        'bg-background/60 backdrop-blur-[2px]',
        'flex items-center justify-center',
        'animate-in fade-in duration-200',
        className,
      )}
    >
      <div className="flex flex-col items-center gap-3">
        {/* Modern pulsing loader */}
        <div className="relative flex items-center justify-center h-12 w-12">
          {/* Outer ring pulse */}
          <div
            className="absolute inset-0 rounded-full border-2 border-primary/20"
            style={{ animation: 'ecg-loader-pulse 1.5s ease-in-out infinite' }}
          />
          {/* Spinning arc */}
          <svg
            className="h-10 w-10 animate-spin"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="20" cy="20" r="17"
              stroke="hsl(var(--primary) / 0.15)"
              strokeWidth="3"
            />
            <path
              d="M20 3 a17 17 0 0 1 17 17"
              stroke="hsl(var(--primary))"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </div>
        {text && (
          <p className="text-sm text-muted-foreground font-medium animate-pulse">
            {text}
          </p>
        )}
      </div>
      <style>{`
        @keyframes ecg-loader-pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.2); opacity: 0.2; }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// Full Page Loading (used by AppLayout during navigation)
// ============================================================================

export const FullPageLoading: React.FC<{ text?: string }> = ({ text }) => {
  return (
    <>
      <NavigationProgressBar />
      <NavigationLoadingOverlay text={text} />
    </>
  );
};

// ============================================================================
// Form Loading State
// ============================================================================

export const FormLoadingSkeleton: React.FC = () => {
  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <Skeleton width={128} height={128} circle className="mx-auto mb-4" />
        <Skeleton width="60%" height={32} className="mx-auto mb-2" />
        <Skeleton width="80%" height={20} className="mx-auto" />
      </div>

      {/* Form Sections */}
      <div>
        <Skeleton width="40%" height={24} className="mb-4" />
        <FormFieldSkeleton count={2} />
      </div>

      <div>
        <Skeleton width="40%" height={24} className="mb-4" />
        <FormFieldSkeleton count={2} />
      </div>

      {/* Submit Button */}
      <Skeleton width="100%" height={48} />
    </div>
  );
};

// ============================================================================
// Default Export
// ============================================================================

export default Skeleton;
